import React, { useState, useEffect } from 'react';
import { 
  FaCheckCircle, 
  FaTrash, 
  FaImage, 
  FaEye, 
  FaSpinner 
} from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../security/security/AuthContext';

const AdminProofs = () => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchProofs();
  }, []);

  const fetchProofs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.visiontech2030.com/api/admin/proofs', {
        headers: { Authorization: token }
      });
      setProofs(response.data);
    } catch (error) {
      console.error('Error fetching proofs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProof = async (proofId) => {
    try {
      await axios.put(`https://api.visiontech2030.com/api/admin/${proofId}/approve`, null, {
        headers: { Authorization: token }
      });
      // Refresh proofs list after approval
      fetchProofs();
    } catch (error) {
      console.error('Error approving proof:', error);
    }
  };

  const handleDeleteProof = async (proofId) => {
    if (!window.confirm('Are you sure you want to delete this proof? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(proofId);
      await axios.delete(`https://api.visiontech2030.com/api/admin/${proofId}/reject`, {
        headers: { Authorization: token }
      });
      // Remove the proof from local state immediately
      setProofs(prevProofs => prevProofs.filter(proof => proof.id !== proofId));
      
      // Close modal if the deleted proof was being viewed
      if (selectedProof && selectedProof.id === proofId) {
        setShowImageModal(false);
        setSelectedProof(null);
      }
    } catch (error) {
      console.error('Error deleting proof:', error);
      alert('Failed to delete proof. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const ImageModal = ({ proof, onClose }) => (
    <div className="adminpage-proof-modal-overlay">
      <div className="adminpage-proof-modal">
        <button className="adminpage-proof-modal-close" onClick={onClose}>×</button>
        <div className="adminpage-proof-modal-content">
          <img 
            src={`https://api.visiontech2030.com${proof.imageUrl}`} 
            alt="Proof" 
            className="adminpage-proof-modal-image" 
          />
          <div className="adminpage-proof-modal-details">
            <h3>Proof Details</h3>
            <p><strong>Description:</strong> {proof.description}</p>
            <p><strong>Phone Number:</strong> {proof.phoneNumber}</p>
            <p><strong>Status:</strong> {proof.status}</p>
            <p><strong>Submitted:</strong> {format(new Date(proof.createdAt), 'PPp')}</p>
            <div className="adminpage-proof-modal-actions">
              <button 
                className="adminpage-btn-approve"
                onClick={() => handleApproveProof(proof.id)}
                disabled={proof.status === 'APPROVED'}
              >
                <FaCheckCircle /> Approve
              </button>
              <button 
                className="adminpage-btn-delete"
                onClick={() => handleDeleteProof(proof.id)}
                disabled={deleting === proof.id}
              >
                {deleting === proof.id ? <FaSpinner className="adminpage-spinner" /> : <FaTrash />}
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="adminpage-proofs">
      <div className="adminpage-card">
        <div className="adminpage-card-header">
          <h5>Transaction Proofs Management</h5>
        </div>
        <div className="adminpage-card-body">
          {loading ? (
            <div className="adminpage-loading">
              <FaSpinner className="adminpage-spinner" />
              <p>Loading proofs...</p>
            </div>
          ) : (
            <table className="adminpage-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Description</th>
                  <th>Phone Number</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proofs.map((proof) => (
                  <tr key={proof.id}>
                    <td>{proof.userEmail}</td>
                    <td>
                      <div className="adminpage-description-cell">
                        {proof.description}
                      </div>
                    </td>
                    <td>{proof.phoneNumber}</td>
                    <td>
                      <span className={`adminpage-status adminpage-status-${proof.status.toLowerCase()}`}>
                        {proof.status}
                      </span>
                    </td>
                    <td>{format(new Date(proof.createdAt), 'PP')}</td>
                    <td>
                      <div className="adminpage-proof-actions">
                        <button 
                          className="adminpage-btn-view"
                          onClick={() => {
                            setSelectedProof(proof);
                            setShowImageModal(true);
                          }}
                        >
                          <FaEye />
                        </button>
                        <button 
                          className="adminpage-btn-approve"
                          onClick={() => handleApproveProof(proof.id)}
                          disabled={proof.status === 'APPROVED'}
                        >
                          <FaCheckCircle />
                        </button>
                        <button 
                          className="adminpage-btn-delete"
                          onClick={() => handleDeleteProof(proof.id)}
                          disabled={deleting === proof.id}
                        >
                          {deleting === proof.id ? <FaSpinner className="adminpage-spinner" /> : <FaTrash />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {showImageModal && selectedProof && (
        <ImageModal 
          proof={selectedProof} 
          onClose={() => {
            setShowImageModal(false);
            setSelectedProof(null);
          }} 
        />
      )}
    </div>
  );
};

export default AdminProofs;
